import { Button } from "@/ingot";
import { useTranslation } from "react-i18next";

import { usePriceFrom } from "@/api/queries/pricing";
import { useQuoteManufacturability } from "@/api/queries/quotes";
import { OperationIcon } from "@/components/cart/OperationIcon";
import { WarningsPanel } from "@/components/WarningsPanel";

import { useDisplayMoney } from "@/lib/currency";
import { minorToMajor } from "@/lib/money";
import { MetricsView } from "@/processes/dynamic/InteractiveViewer";
import type { Process, Quote } from "@/api/types";

interface EstimateCardProps {
  quote: Quote;
  /** Registry row for ``quote.process_type`` — icon + display name. */
  process: Process | null;
  /** Chain-inclusive registry (``include_non_entry=true``) — resolves the
   *  display names of ``quote.suggested_chain`` operations. Optional: the
   *  suggested-flow row falls back to raw process_type strings without it. */
  allProcesses?: Process[];
  lang: string;
  onOpenConfigurator: () => void;
  /** "Nahrát jiný soubor" — drop the estimate and show the dropzone again. */
  onReset: () => void;
}

/**
 * „Hotový odhad" — the homepage estimate card shown in place of the dropzone
 * right after the upload's analysis finishes, BEFORE the configurator opens.
 *
 * Content is schema-driven end to end: the rows come pre-resolved from the
 * process's ``estimate_summary_schema`` (``quote.estimate_summary.fields``,
 * built BE-side by the same resolver as the "Vizualizace" metrics table and
 * rendered by the same ``MetricsView`` the admin preview uses), and the
 * "cena od" row queries ``POST /pricing/price-from`` (cheapest material for
 * the process) only when the schema opts in via ``show_price_from``.
 */
export function EstimateCard({
  quote,
  process,
  allProcesses,
  lang,
  onOpenConfigurator,
  onReset,
}: EstimateCardProps): JSX.Element {
  const { t, i18n } = useTranslation("common");
  // KAN-1818 — měna a formát podle nastavení obchodu a jazyka stránky;
  // dřív pevné „Kč“ i na /en.
  const { format } = useDisplayMoney(i18n.language);
  const summary = quote.estimate_summary ?? null;
  // Auto-routing recommendation (tenant chain_recommendation rules) —
  // "primary → op → op" steps with registry display names.
  const suggestedSteps = (() => {
    const items = quote.suggested_chain?.suggested_chain ?? [];
    if (items.length === 0) return [];
    const nameFor = (processType: string): string =>
      allProcesses?.find((p) => p.name === processType)?.display_name ??
      processType;
    const primary = process?.display_name ?? nameFor(quote.process_type ?? "");
    return [primary, ...items.map((item) => nameFor(item.process_type))];
  })();
  // KAN-1298 — a part the operation cannot make is known right after the
  // analysis; the card says why and offers no "cena od" for it.
  const manufacturability = useQuoteManufacturability(quote.id, null, null);
  // KAN-1864 — the analysis failed outright (the file is not a drawing the
  // analyzer can read): nothing was measured, so there is no part to
  // configure. Read from the analysis contract's status, not the file type.
  const unreadable = quote.analysis?.status === "ERROR";
  const blocked = unreadable || manufacturability.data?.blocking === true;
  // KAN-1874 — what the analyzer says about the file itself (e.g. a drawing
  // without units read as mm, with the part's size) belongs on the card even
  // when nothing blocks: the card is where the customer first reads the
  // price, and a wrong-unit drawing shows only in the size.
  const fileWarnings = quote.analysis?.warnings ?? [];
  const priceFrom = usePriceFrom(
    summary?.show_price_from && quote.process_type && !blocked
      ? { process_type: quote.process_type, analysis: quote.analysis, lang }
      : null,
  );
  const price = priceFrom.data ?? null;

  return (
    <div
      className="rounded-2xl border-2 border-dashed border-border-strong bg-surface px-6 py-8 text-left sm:px-10"
      data-testid="estimate-card"
    >
      <div className="flex items-center gap-4">
        <div
          aria-hidden="true"
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-accent-bg text-accent-ink"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="truncate font-mono text-sm font-medium text-ink"
            data-testid="estimate-card-file"
          >
            {quote.file_name}
          </div>
          {process && (
            <div className="mt-0.5 flex items-center gap-1.5 text-sm text-ink-3">
              <OperationIcon
                iconUrl={process.icon_url}
                slug={process.slug}
                displayName={process.display_name}
                size={14}
              />
              {process.display_name}
            </div>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onReset}
          data-testid="estimate-card-reset"
        >
          {t("home.estimate.changeFile")}
        </Button>
      </div>

      {/* KAN-1864 — an unreadable file measured nothing; "Délka řezu 0 mm"
          would read as a measurement. */}
      {!unreadable && summary && summary.fields.length > 0 && (
        <div className="mt-5" data-testid="estimate-card-fields">
          <MetricsView data={{ metrics: summary.fields }} />
        </div>
      )}

      {!unreadable && suggestedSteps.length > 0 && (
        <div
          className="mt-4 rounded-lg border border-border bg-surface-2 px-4 py-3"
          data-testid="estimate-card-suggested-chain"
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-3">
            {t("home.estimate.suggestedChain")}
          </span>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm font-medium text-ink">
            {suggestedSteps.map((name, i) => (
              <span key={`${name}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && (
                  <span aria-hidden="true" className="text-ink-4">
                    →
                  </span>
                )}
                {name}
              </span>
            ))}
          </div>
        </div>
      )}

      {(blocked || fileWarnings.length > 0) && (
        <div
          className="mt-4"
          data-testid={blocked ? "estimate-card-blocked" : "estimate-card-warnings"}
        >
          {/* KAN-1864 — the analysis' own findings travel along: an
              unreadable file is explained by the analyzer's message, and a
              finding that explains a violated limit (an open contour with its
              place) replaces the generic limit text. */}
          <WarningsPanel
            warnings={fileWarnings}
            status={unreadable ? "ERROR" : "OK"}
            manufacturabilityWarnings={manufacturability.data?.warnings ?? []}
            blocking={blocked}
          />
        </div>
      )}

      {!blocked &&
        summary?.show_price_from &&
        (priceFrom.isPending || price?.price_from_czk != null) && (
          <div
            className="mt-4 flex items-baseline justify-between gap-3 rounded-lg border border-border bg-surface-2 px-4 py-3"
            data-testid="estimate-card-price"
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-3">
              {t("home.estimate.priceFromLabel")}
            </span>
            {priceFrom.isPending ? (
              <span className="text-sm text-ink-3">
                {t("home.estimate.pricePending")}
              </span>
            ) : (
              <span className="text-base font-semibold text-ink">
                {t("home.estimate.priceFromValue", {
                  price: format(price?.price_from_czk ?? 0),
                })}
                {price?.material_name && (
                  <span className="ml-2 text-sm font-normal text-ink-3">
                    · {price.material_name}
                  </span>
                )}
                {/* KAN-1854 — „od X/ks" je bez přípravy zakázky; ta se platí
                    jednou za zakázku a u jednoho kusu je větší než cena
                    kusu. Částku posílá server ze stejného ceníku. */}
                {price?.setup_fee_minor != null && price.setup_fee_minor > 0 && (
                  <span
                    className="block text-sm font-normal text-ink-3"
                    data-testid="estimate-card-setup-fee"
                  >
                    {t("home.estimate.priceFromSetupFee", {
                      fee: format(minorToMajor(price.setup_fee_minor, price.currency_code)),
                    })}
                  </span>
                )}
              </span>
            )}
          </div>
        )}

      {!unreadable && (
        <Button
          variant="primary"
          size="lg"
          onClick={onOpenConfigurator}
          className="mt-5 w-full justify-center"
          data-testid="estimate-card-open-configurator"
        >
          {t("home.estimate.openConfigurator")} →
        </Button>
      )}
    </div>
  );
}
